/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe, NgTemplateOutlet } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { ButtonComponent,
    CodeSnippetComponent, ScrollContainerComponent,
    LinkPanelComponent, ModalComponent, PropertiesTableComponent, PropertiesTableRowComponent, SpinnerComponent, FormPasswordInputComponent, requiredValidator, FormComponent, FormActionsComponent, ModalCloseButtonComponent, FormInputComponent
} from "typedb-cloud-framework";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { SnackbarService } from "../../../../service/snackbar.service";
import { Cluster, Server } from "../../../../concept/cluster";
import { Observable, Subject, first } from "rxjs";
import { MatSelectModule } from "@angular/material/select";
import { MatStepperModule } from "@angular/material/stepper";
import { FormBuilder } from "@angular/forms";
import { ClusterApi } from "../../../../service/cluster/cluster-api.service";
import { cryptoRandomAlphanumeric } from "../../../../util";
import { environment } from "../../../../environments/environment";
import { buildConnectionString, clusterCredentialFilename, parseVersionString } from "../shared";

export type SetupDialogData = {
    cluster$: Observable<Cluster>,
    servers$: Observable<Server[]>,
    password: string | undefined,
    updatePassword: (newPassword: string | undefined) => void,
}

@Component({
    selector: "tp-cluster-setup-dialog",
    templateUrl: "./cluster-setup-dialog.component.html",
    styleUrls: ["./cluster-setup-dialog.component.scss"],
    imports: [
        AsyncPipe, MatTooltipModule, MatButtonModule, MatSelectModule, ButtonComponent, ScrollContainerComponent,
        MatStepperModule, MatDialogContent, MatDialogTitle, FormPasswordInputComponent, ModalCloseButtonComponent,
        FormInputComponent
    ]
})
export class ClusterSetupDialogComponent {
    credentialsDownloading = false;

    passwordForm = this.formBuilder.nonNullable.group({
        username: ["admin"],
        password: ["", [requiredValidator]]
    });
    isPasswordFormSubmitting$ = new Subject<boolean>();
    isPasswordDownloaded = false;
    password: string | undefined = undefined;

    constructor(
        private dialogRef: MatDialogRef<ClusterSetupDialogComponent>, private formBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: SetupDialogData, private snackbar: SnackbarService,
        private clusterApi: ClusterApi,
    ) {
        if (this.data.password) {
            this.passwordForm.patchValue({ password: this.data.password });
        } else {
            this.generatePassword();
        }

        this.passwordForm.controls.password.valueChanges.subscribe((password) => {
            this.isPasswordDownloaded = false;
            this.data.updatePassword(password);
        });
    }

    downloadCredentials() {
        if (!this.passwordForm.value.password) return;
        this.credentialsDownloading = true;
        const password = this.passwordForm.value.password;
        this.data.cluster$.pipe(first())
            .subscribe((cluster) => {
                const version = parseVersionString(cluster.typeDBVersion.version);
                const addresses = this.computeClusterAddresses(cluster);
                const properties = [
                    "username = admin",
                    `password = ${password}`,
                    `addresses = [${addresses.join(", ")}]`,
                    `connectionString = ${buildConnectionString(password, addresses, version, cluster.id)}`
                ];
                const blob = new Blob([properties.join("\n")], { type: "text/plain" });
                const anchor = document.createElement("a");
                anchor.href = URL.createObjectURL(blob);
                anchor.download = clusterCredentialFilename(cluster);
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
                this.isPasswordDownloaded = true;
                this.credentialsDownloading = false;
                this.snackbar.success("Downloaded", { duration: 1250 });
                this.submitPasswordForm(true);
            });
    }

    close() {
        this.dialogRef.close();
    }

    generatePassword() {
        this.passwordForm.patchValue({ password: cryptoRandomAlphanumeric(16) });
    }

    submitPasswordForm(passwordDownloaded: boolean) {
        this.passwordForm.markAllAsTouched();
        if (this.passwordForm.invalid) return;
        this.passwordForm.disable();
        this.isPasswordFormSubmitting$.next(true);
        this.data.cluster$.pipe(first()).subscribe((cluster) => {
            this.password = this.passwordForm.value.password!;
            this.data.updatePassword(this.password);
            this.clusterApi.setupCluster({ clusterUuid: cluster.uuid, password: this.password }).subscribe({
                next: () => {
                    if (!passwordDownloaded) this.snackbar.success("Password set");
                    this.isPasswordFormSubmitting$.next(false);
                    this.close();
                },
                error: () => {
                    this.password = undefined;
                    this.isPasswordFormSubmitting$.next(false);
                    this.passwordForm.enable();
                }
            });
        });
    }

    computeClusterAddresses(cluster: Cluster): string[] {
        let clusterURL: string;
        if (environment.env === "production") {
            clusterURL = "cluster.typedb.com"
        } else if (environment.env === "development" || environment.env === "local") {
            clusterURL = "cluster.typedb.dev"
        }
        return new Array(cluster.serverCount).fill(null)
            .map((_, i) => `https://${cluster.shortId}-${i}.${clusterURL}:80`);
    }
}
